import React, { useState, useEffect } from 'react';
import HamburgerMenu from '../components/HamburgerMenu';
import ManageUsersModal from '../components/ManageUsersModal';
import '../components/Calendar.css';

const MONTH_NAMES = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December',
];
const DAY_HEADERS = ['Su','Mo','Tu','We','Th','Fr','Sa'];

const TYPE_COLOR = {
  vacation:   '#fb923c',
  booked:     '#60a5fa',
  band_event: '#60a5fa',
  practice:   '#4ade80',
};

function buildMiniDays(year, month) {
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const days = [];
  for (let i = 0; i < firstDay; i++) days.push(null);
  for (let d = 1; d <= daysInMonth; d++) days.push(d);
  while (days.length % 7 !== 0) days.push(null);
  return days;
}

function eventTypesForDay(year, month, day, events) {
  const dayStart = new Date(year, month, day, 0, 0, 0);
  const dayEnd   = new Date(year, month, day, 23, 59, 59);
  const types = new Set();
  events.forEach(e => {
    const s  = e.start instanceof Date ? e.start : new Date(e.start);
    const en = e.end   instanceof Date ? e.end   : new Date(e.end);
    if (s <= dayEnd && en >= dayStart) types.add(e.type || 'band_event');
  });
  return [...types];
}

const MiniMonth = ({ year, month, events }) => {
  const days = buildMiniDays(year, month);
  const today = new Date();
  const isCurrentMonth = today.getFullYear() === year && today.getMonth() === month;

  return (
    <div className="bg-gray-700 rounded-lg p-3">
      <div className={`text-center text-sm font-bold mb-2 ${isCurrentMonth ? 'text-blue-400' : 'text-white'}`}>
        {MONTH_NAMES[month]}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 1 }}>
        {DAY_HEADERS.map(h => (
          <div key={h} style={{ textAlign: 'center', fontSize: 9, color: '#6b7280', fontWeight: 700, paddingBottom: 3 }}>
            {h}
          </div>
        ))}
        {days.map((day, i) => {
          if (!day) return <div key={i} />;
          const types = eventTypesForDay(year, month, day, events);
          const isToday = isCurrentMonth && today.getDate() === day;
          // Priority: holiday > gig > practice
          const priorityOrder = ['vacation', 'band_event', 'booked', 'practice'];
          const topType = priorityOrder.find(t => types.includes(t)) || types[0];
          const hasEvent = !!topType;
          const bgColor = hasEvent ? TYPE_COLOR[topType] : 'transparent';
          return (
            <div key={i} style={{ textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2px 0' }}>
              <span style={{
                fontSize: isToday && !hasEvent ? 12 : 10,
                fontWeight: hasEvent || isToday ? 700 : 400,
                color: hasEvent ? '#0d0515' : isToday ? '#ffffff' : '#d1d5db',
                background: bgColor,
                borderRadius: '50%',
                width: 20,
                height: 20,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: hasEvent ? `0 0 7px ${bgColor}` : 'none',
                textShadow: isToday && !hasEvent ? '0 0 8px #fff' : 'none',
              }}>
                {day}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const YearView = ({ user }) => {
  const [year, setYear] = useState(new Date().getFullYear());
  const [events, setEvents] = useState([]);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isManageUsersModalOpen, setIsManageUsersModalOpen] = useState(false);

  useEffect(() => {
    fetch('/api/events')
      .then(res => res.json())
      .then(data => setEvents(data.map(e => ({ ...e, start: new Date(e.start), end: new Date(e.end) }))));
  }, []);

  const handleLogout = () => { window.location.href = '/logout'; };
  const handleOpenManageUsersModal = () => { setIsManageUsersModalOpen(true); setIsMenuOpen(false); };
  const handleCloseManageUsersModal = () => setIsManageUsersModalOpen(false);

  return (
    <div className="flex flex-col min-h-screen bg-gray-800 text-white p-4">
      <div className="flex justify-between items-center mb-1">
        <button className="icon-button hamburger-icon" onClick={() => setIsMenuOpen(o => !o)}>&#9776;</button>
        <span className="calendar-title">Crazy Cow Calendar</span>
        <div style={{ width: 40 }} />
        {isMenuOpen && (
          <HamburgerMenu
            user={user}
            onLogout={handleLogout}
            onManageUsersClick={handleOpenManageUsersModal}
            onClose={() => setIsMenuOpen(false)}
          />
        )}
      </div>
      <div className="flex justify-center items-center gap-3 mb-4">
        <button onClick={() => setYear(y => y - 1)} className="text-white text-xl px-2">&lt;</button>
        <h1 className="text-xl font-bold">{year}</h1>
        <button onClick={() => setYear(y => y + 1)} className="text-white text-xl px-2">&gt;</button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
        {Array.from({ length: 12 }, (_, i) => (
          <MiniMonth key={i} year={year} month={i} events={events} />
        ))}
      </div>

      <div className="flex gap-6 mt-4 justify-center text-xs text-gray-400">
        {[
          { label: 'Gig',      color: TYPE_COLOR.band_event },
          { label: 'Practice', color: TYPE_COLOR.practice },
          { label: 'Holiday',  color: TYPE_COLOR.vacation },
        ].map(({ label, color }) => (
          <span key={label} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            <span style={{ width: 8, height: 8, borderRadius: '50%', background: color, display: 'inline-block' }} />
            {label}
          </span>
        ))}
      </div>

      {isManageUsersModalOpen && (
        <ManageUsersModal onClose={handleCloseManageUsersModal} currentUser={user} />
      )}
    </div>
  );
};

export default YearView;
